
























































































import { mapGetters } from 'vuex';

import {
  getBetVolumesConfiguration,
  updateBetVolumesConfiguration
} from '@/api/CopyStake/Settings';
import {
  ValidationRules,
  TCopyStakeSettingsBetVolumesResponse,
  TCopyStakeSettingsBetVolumesPayload
} from '@/api/schema';

import { errorToastMessage } from '@/helpers/errorToastMessage';
import * as vr from '@/helpers/validation';
import { cloneDeep, isEqual } from 'lodash';
import type { AxiosError } from 'axios';

export default {
  name: 'CopyStakeSettingsBetAmount',
  data(): any {
    return {
      currentCurrency: 'USD',

      betVolumes: {
        USD: {
          min: null,
          max: null,
          defaultValue: null,
          aiStreamerMaxBetVolume: null
        }
      },
      betVolumesSavedData: {
        USD: {
          min: null,
          max: null,
          defaultValue: null,
          aiStreamerMaxBetVolume: null
        }
      },

      isLoading: false,
      formValid: false
    };
  },
  props: {
    title: {
      type: String,
      default: (): string => ''
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),

    canEdit(): boolean {
      return this.$role.can.update('copy_stake streamers');
    },

    limitsChanged(): boolean {
      return !isEqual(this.betVolumes, this.betVolumesSavedData);
    },
    valueCanBeSaved(): boolean {
      return this.formValid && this.limitsChanged;
    },

    currencyList(): string[] {
      return Object.keys(this.betVolumes);
    },

    isOptionalBetVolumesValues(): boolean {
      const currentBetVolumes = this.betVolumes[this.currentCurrency];

      return (
        this.currentCurrency !== 'USD' &&
        [
          currentBetVolumes.max,
          currentBetVolumes.min,
          currentBetVolumes.defaultValue
        ].every((v) => v === null)
      );
    },

    rules(): ValidationRules {
      const { max, min } = this.betVolumes[this.currentCurrency];

      const baseValidations = [
        (v: any) => vr.validNumber(v),
        (v: any) => vr.positiveNumber(v)
      ];

      return {
        maxBetRules:
          this.currentCurrency === 'USD'
            ? [
                (v) => v === null || vr.validNumber(v),
                (v) => v === null || vr.positiveNumber(v)
              ]
            : this.isOptionalBetVolumesValues
            ? []
            : baseValidations,

        minBetRules: this.isOptionalBetVolumesValues
          ? []
          : [
              ...baseValidations,
              (v) => !max || vr.lte(v, max, "Min Bet can't exceed Max Bet")
            ],

        defaultBetRules: this.isOptionalBetVolumesValues
          ? []
          : [
              ...baseValidations,
              (v) =>
                vr.gte(
                  v,
                  min,
                  'Default Bet Amount cannot be lower than Min Bet'
                ),
              (v) =>
                !max ||
                vr.lte(v, max, "Default Bet Amount can't exceed Max Bet")
            ],

        aiStreamerMaxBetRules: [
          (v) => v === null || vr.validNumber(v),
          (v) => v === null || vr.positiveNumber(v)
        ]
      };
    }
  },
  watch: {
    operatorId: {
      handler: 'getConfiguration',
      immediate: true
    }
  },
  methods: {
    onBetVolumesInput(val: string | number, betVolumesKey: string): void {
      this.betVolumes[this.currentCurrency][betVolumesKey] =
        this.parseInputValue(val);

      this.validateForm();
    },
    parseInputValue(val: string | number): number | string | null {
      if (val === '') return null;

      const num = Number(val);
      return isNaN(num) ? val : num;
    },

    validateForm(): void {
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },

    parseSettingsData(data: TCopyStakeSettingsBetVolumesResponse): void {
      this.betVolumesSavedData = cloneDeep(data);
      this.betVolumes = cloneDeep(data);
    },

    async getConfiguration(): Promise<void> {
      if (!this.operatorId) {
        return;
      }

      this.isLoading = true;

      getBetVolumesConfiguration()
        .then((data: TCopyStakeSettingsBetVolumesResponse) => {
          this.parseSettingsData(data);
        })
        .catch((err: AxiosError): void => {
          errorToastMessage(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async updateConfiguration(): Promise<void> {
      const isFormValid = await this.$refs.form.validate();
      if (!isFormValid) return;

      this.isLoading = true;

      const payload: TCopyStakeSettingsBetVolumesPayload = this.betVolumes;

      await updateBetVolumesConfiguration(payload)
        .then((data: TCopyStakeSettingsBetVolumesResponse) => {
          this.parseSettingsData(data);

          this.$toast.success('Bet Volumes was successfully updated!');
        })
        .catch((err: AxiosError): void => {
          errorToastMessage(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
